'use client';

import { forwardRef, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Select as MantineSelect } from '@mantine/core';

export interface ISelect {
  id?: string;
  name?: string;
  label?: string;
  icon?: ReactNode;
  ref?: string | null;
  data: unknown;
  size?: 'sm' | 'md' | 'lg';
  externalValue?: string | { [x: string]: string } | null;
  onCreate?: (value: string) => void;
  onSelectChange?: (value: string) => void;
  resetSelect?: boolean;
  additionalClassNames?: string;
  additionalInputClassNames?: string;
  isFormatted?: boolean;
  isClearable?: boolean;
  isCreatable?: boolean;
  isSearchable?: boolean;
  disabled?: boolean;
  placeholder?: string;
  dataTestId?: string;
  required?: boolean;
  rightSection?: React.ReactNode;
  leftSection?: React.ReactNode;
  error?: string | null;
  withinPortal?: boolean;
  zIndex?: number;
}

const BaseClasses = {
  wrapper: '!h-9',
  rightSection: '!h-9',
  leftSection: '!h-9',
};

export const Select = forwardRef<HTMLInputElement, ISelect>(
  (
    {
      id,
      name,
      label,
      icon,
      data,
      size,
      externalValue,
      onSelectChange,
      onCreate,
      resetSelect,
      additionalClassNames,
      additionalInputClassNames = '',
      isFormatted,
      isClearable = true,
      isCreatable,
      isSearchable,
      disabled,
      placeholder,
      dataTestId = 'selector',
      required,
      rightSection,
      error,
      withinPortal = true,
      zIndex = 1000,
    }: ISelect,
    ref,
  ) => {
    const [value, setValue] = useState<string | undefined>('');
    const [appendedData, setAppendedData] = useState<string[]>([]);

    const { t } = useTranslation();

    // In order to change the Select value from outside sources
    useEffect(() => {
      if (typeof externalValue === 'object') setValue(externalValue?.value);
      else setValue(String(externalValue));
    }, [externalValue]);

    useEffect(() => {
      if (resetSelect) setValue('');
    }, [resetSelect]);

    useEffect(() => {
      // Catching new creation
      if (!data || typeof data === 'string') return;

      // Sometimes we need to format the data before it reaches this Component as the data isn't generic enough.
      setAppendedData(
        isFormatted
          ? data
          : // @ts-expect-error need to change this if its an array
            data?.map((item: { id: number; title: string }) => ({
              value: item.id.toString(),
              label: item.title,
            })),
      );
    }, [data, isFormatted]);

    if (!appendedData) return <></>;

    return (
      <MantineSelect
        autoComplete="off"
        id={id}
        ref={ref}
        name={name}
        label={label}
        placeholder={placeholder}
        icon={icon}
        data-testid={dataTestId}
        data={appendedData}
        size={size}
        className={additionalClassNames}
        rightSection={rightSection}
        rightSectionWidth={30}
        required={required}
        classNames={{
          ...BaseClasses,
          input: `${additionalInputClassNames} !h-9`,
        }}
        styles={(theme) => ({
          item: {
            // applies styles to selected item
            '&[data-selected]': {
              backgroundColor: theme?.colors?.brand?.[2],
              color: '#000',
              '&, &:hover': {
                backgroundColor: theme?.colors?.brand?.[2],
                color: '#000',
              },
            },

            // applies styles to hovered item (with mouse or keyboard)
            '&[data-hovered]': {
              backgroundColor: theme?.colors?.brand?.[2],
              color: '#000',
            },
          },
        })}
        getCreateLabel={(query) => `+ Create ${query}`}
        onCreate={(query: string) => {
          setAppendedData((current) => [...current, query]);
          onCreate && onCreate(query);
          return null;
        }}
        clearable={isClearable}
        creatable={isCreatable}
        searchable={isSearchable}
        nothingFound={t('globals:noOptions')}
        disabled={disabled}
        shadow="md"
        radius="sm"
        maxDropdownHeight={280}
        value={value}
        onChange={(value: string) => {
          resetSelect = false;
          setValue(value);
          onSelectChange && onSelectChange(value);
        }}
        error={error}
        withinPortal={withinPortal}
        zIndex={zIndex}
      />
    );
  },
);

Select.displayName = 'Select';
